import React, { useEffect, useState } from "react";
import Layout from "../../../components/Layout";
import { useTranslation } from "react-i18next";
import FormRepeater from "../../../components/form/FormRepeater";
import { CheckCardBalance } from "../../../api/sell-orders/checkCardBalance";
import MessageError from "../../../components/MessageError";
import { Link } from "react-router-dom";
import CustomTooltip from "../../../components/CustomTooltip";
import {
  DeleteIcon,
  DownloadIcon,
  EyeOpen,
  LoadingIcon,
  PrintIcon,
} from "../../../assets/icons/Icons";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import { useUserContext } from "../../../contexts/UserContext";
import usePrint from "../../../hooks/usePrint";
import { getReceipt } from "../../../api/sell-orders/getReceipt";
import ViewSellOrder from "../../../components/sell-order/View";
import { GetSellOrders } from "../../../api/sell-orders/getSellOrders";
import ViewBatchSellOrder from "../../../components/batch-order/View";
import { PrintGiftIndividualCard } from "../../../components/GiftReceipt";

function Index(props) {
    
  const { t } = useTranslation();

  const [cards, setCards] = useState([]);
  const [showViewItem, setShowViewItem] = useState(null);

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Check Card Balance")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <Layout title={t("Check Card Balance")} breadcrumbs={t("Card Issuing")}>
      {showViewItem && (
        <>
          {showViewItem.type === "batch" ? (
            <ViewBatchSellOrder
              order={showViewItem}
              onClose={() => setShowViewItem(false)}
            />
          ) : (
            <ViewSellOrder
              order={showViewItem}
              onClose={() => setShowViewItem(false)}
            />
          )}
        </>
      )}

      <FormRepeater
        columns={[
          t("Proxy Number"),
          "",
          t("Product Description"),
          t("Balance"),
          t("Action"),
        ]}
        maxRows={5}
        addRowText={t("Add Card")}
        RenderComponent={CheckCardBalanceRow}
        onRowChange={setCards}
        rowData={cards}
        setShowViewItem={setShowViewItem}
      />
    </Layout>
  );
}

export default Index;

const CheckCardBalanceRow = ({
  rowData,
  rowIndex,
  onFieldChange,
  removeRow,
  setShowViewItem,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { hasPermission, hasDynamicPermissions } = useUserContext();

  const { handlePrint: handlePrintReceipt } = usePrint(getReceipt);
  const canCheck = hasDynamicPermissions()
    ? hasPermission("sell_order.check_card_balance", "write")
    : true;

  const handleCheckButton = async (e) => {
    if (!canCheck) return;
    let prn = rowData.proxy_number;
    if (prn) {
      try {
        setLoading(true);
        setError("");
        const [sellOrder, cardBalance] = await Promise.all([
          GetSellOrders({ prn, type: "regular,batch" }),
          CheckCardBalance({ prn }),
        ]);
        const orderInfo = sellOrder?.data?.[0];
        onFieldChange({
          description: "Oxford Gift Card PLUS",
          amount: cardBalance?.data?.balance ?? "",
          sell_order_id: sellOrder?.data?.[0]?.id ?? null,
          ...orderInfo,
        });
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err.message);
        console.log("Error fetching data: " + err);
      }
    }
  };

  useEffect(() => {
    setError("");
    onFieldChange({
      description: "",
      amount: "",
      sell_order_id: null,
    });
    // eslint-disable-next-line
  }, [rowData.proxy_number]);

  return (
    <>
      <td>
        <input
          type="number"
          placeholder={t("Proxy Number")}
          value={rowData.proxy_number || ""}
          onChange={(e) => onFieldChange("proxy_number", e.target.value)}
          min={0}
          style={{ minWidth: "200px" }}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
        />
        <MessageError error={error} />
      </td>
      <td>
        <button
          className={`btn btn-primary btn-small ${
            loading ? "btn-loading" : ""
          }`}
          onClick={handleCheckButton}
          disabled={!rowData.proxy_number || !canCheck}
          title={
            !canCheck
              ? t("You do not have permission to check card balances.")
              : undefined
          }
        >
          {loading && <LoadingIcon />}
          {t("Check")}
        </button>
      </td>
      <td>
        <input
          type="text"
          placeholder={t("Card Type")}
          value={rowData.description || ""}
          onChange={(e) => onFieldChange("description", e.target.value)}
          disabled
          style={{ minWidth: "200px" }}
        />
      </td>
      <td>
        <input
          type="number"
          placeholder={t("Amount")}
          value={rowData.amount || ""}
          onChange={(e) => onFieldChange("amount", e.target.value)}
          disabled
          style={{ minWidth: "120px" }}
          onWheel={(e) => e.target.blur()}
          onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault(); }}
        />
      </td>
      <td className="text-center d-flex align-items-center">
        {rowData?.sell_order_id && (
          <div className="d-flex align-items-center">
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handlePrintReceipt({
                  requestParams: { id: rowData.sell_order_id, type: rowData.type },
                  name: `order-${rowData.sell_order_id}`,
                  width: "",
                  height: "",
                  download: false,
                });
              }}
            >
              <CustomTooltip
                icon={<PrintIcon />}
                message={t("Print Receipt")}
              />
            </Link>
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                handlePrintReceipt({
                  requestParams: { id: rowData.sell_order_id, type: rowData.type },
                  name: `order-${rowData.sell_order_id}`,
                  width: "",
                  height: "",
                  download: true,
                });
              }}
            >
              <CustomTooltip
                icon={<DownloadIcon />}
                message={t("Get Receipt")}
              />
            </Link>
            <PrintGiftIndividualCard
              order={rowData}
              prn={rowData.proxy_number}
            />
            <Link
              to="#"
              onClick={(e) => {
                e.preventDefault();
                setShowViewItem(rowData);
              }}
            >
              <CustomTooltip icon={<EyeOpen />} message={t("View")} />
            </Link>
          </div>
        )}
        <Link
          to="#"
          onClick={(e) => {
            e.preventDefault();
            removeRow(rowIndex);
          }}
        >
          <CustomTooltip icon={<DeleteIcon />} message={t("Remove Row")} />
        </Link>
      </td>
    </>
  );
};
